/**
 *  @class
 *  @function ModalDialog
 */
if (!customElements.get('modal-dialog')) {
  // Overlay group sections (data-overlay="popup|age-verification") open
  // themselves. Only one pop-up opens on its own per page view.
  let autoPopupShown = false;
  const overlayFocusable = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, video[controls], audio[controls], [tabindex]:not([tabindex="-1"])';

  class ModalDialog extends HTMLElement {
    constructor() {
      super();

      this.overlay = this.dataset.overlay;
      this.locked = this.overlay === 'age-verification';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);

      this.querySelector('[id^="ModalClose-"]').addEventListener(
        'click',
        this.hide.bind(this)
      );
      this.addEventListener('keyup', (event) => {
        if (!this.locked && event.code && event.code.toUpperCase() === 'ESCAPE') {
          this.hide();
        }
      });
      if (this.overlay) {
        // While open, storefront overlays take Tab and Escape from the document,
        // so the keys still reach them when focus has fallen outside (a click on
        // the backdrop, a return from the address bar).
        this._onKeydown = this.trapFocus.bind(this);
        this._onKeyup = (event) => {
          if (!this.locked && event.key === 'Escape' && this.hasAttribute('open')) this.hide();
        };
        // In the editor the trap stays on the element, so the preview never pulls focus in.
        if (this.designMode) this.addEventListener('keydown', this._onKeydown);
      }
      if (this.locked) {
        this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.store.bind(this));
      }
      if (this.classList.contains('media-modal')) {
        this.addEventListener('pointerup', (event) => {
          if (event.pointerType === 'mouse' && !event.target.closest('product-model')) this.hide();
        });
      } else {
        this.addEventListener('click', (event) => {
          if (!this.locked && event.target.nodeName === 'MODAL-DIALOG') this.hide();
        });
      }
    }

    connectedCallback() {
      if (!this.moved) {
        this.moved = true;
        // In the editor, overlays stay inside their section so they can be selected.
        if (!(this.overlay && this.designMode)) {
          // Moving the element runs disconnectedCallback and connectedCallback again.
          document.body.appendChild(this);
          return;
        }
      }
      if (this.overlay) this.arm();
    }

    disconnectedCallback() {
      if (this.armed) this.disarm();
    }

    arm() {
      this.armed = true;

      if (this.designMode) {
        this._onEditorShow = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.show();
        };
        this._onEditorHide = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.hide();
        };
        document.addEventListener('shopify:section:select', this._onEditorShow);
        document.addEventListener('shopify:section:load', this._onEditorShow);
        document.addEventListener('shopify:block:select', this._onEditorShow);
        document.addEventListener('shopify:section:deselect', this._onEditorHide);
        return;
      }

      if (this.locked) {
        // The section's inline script may already have opened the gate.
        if (this.hasAttribute('open') || !this.suppressed()) this.show();
        return;
      }

      // The pop-up's own sign-up form posted: show its success or error message.
      // Shopify renders the posted state in every customer form on the page, so
      // only the URL hash (the form's action anchor) tells this form apart.
      const signup = this.querySelector('form.signup-form');
      if (signup && signup.querySelector('.form-notification')) {
        const subscribed = !!signup.querySelector('.form-notification.success');
        if (signup.id && window.location.hash === '#' + signup.id) {
          if (subscribed) this.classList.add('popup-modal--signed-up');
          autoPopupShown = true;
          this.store();
          this.show();
          return;
        }
        // Another form posted. Someone who has just subscribed there is not asked
        // again; an error that belongs to that form is cleared from this one.
        if (subscribed) {
          this.store();
          return;
        }
        signup.querySelectorAll('.form-notification').forEach((notice) => notice.remove());
        signup.querySelectorAll('[aria-invalid]').forEach((input) => {
          input.removeAttribute('aria-invalid');
          input.removeAttribute('aria-describedby');
          input.removeAttribute('autofocus');
        });
      }
      if (this.suppressed()) return;

      const delay = Math.max(parseInt(this.dataset.delay, 10) || 0, 0) * 1000;
      this._timer = setTimeout(() => {
        // Exit intent needs a pointer that can leave the window; touch devices use the delay.
        if (this.dataset.trigger === 'exit' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this._onExit = (event) => {
            if (event.relatedTarget || event.clientY > 0) return;
            document.removeEventListener('mouseout', this._onExit);
            this._onExit = null;
            this.showWhenIdle();
          };
          document.addEventListener('mouseout', this._onExit);
        } else {
          this.showWhenIdle();
        }
      }, delay);
    }

    disarm() {
      this.armed = false;
      clearTimeout(this._timer);
      if (this._onExit) {
        document.removeEventListener('mouseout', this._onExit);
        this._onExit = null;
      }
      if (this._onEditorShow) {
        document.removeEventListener('shopify:section:select', this._onEditorShow);
        document.removeEventListener('shopify:section:load', this._onEditorShow);
        document.removeEventListener('shopify:block:select', this._onEditorShow);
        document.removeEventListener('shopify:section:deselect', this._onEditorHide);
      }
      if (this.hasAttribute('open')) {
        this.removeAttribute('open');
        this.releaseLock();
      }
      this.listenForKeys(false);
    }

    showWhenIdle() {
      // Another tab, or a page visited while this one sat in the back/forward
      // cache, may have shown the pop-up since it was armed.
      if (autoPopupShown || this.suppressed()) return;
      // Wait for menus, drawers, search, quick view and lightboxes to close.
      if (ModalDialog.pageBusy()) {
        this._timer = setTimeout(this.showWhenIdle.bind(this), 1000);
        return;
      }
      autoPopupShown = true;
      this.store();
      this.show();
    }

    static pageBusy() {
      const body = document.body.classList;
      return !!document.querySelector('modal-dialog[open], cart-drawer:not([inert]), quick-view.loading, .pswp--open') ||
        body.contains('open-cc') || body.contains('overflow-hidden') || body.contains('open-search');
    }

    show(opener) {
      this.openedBy = opener || document.activeElement;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');

      // Focusing the preview would pull focus out of the editor's settings.
      if (this.overlay && this.designMode) return;
      this.listenForKeys(true);

      setTimeout(() => {
        const invalid = this.overlay && this.querySelector('[aria-invalid="true"]');
        (invalid || this.querySelector('[role="dialog"]')).focus();
      }, 100);
    }

    hide() {
      if (this.querySelector('#Product-Modal-Content') && Shopify.DesignMode) {
        return;
      }
      const hadFocus = this.contains(document.activeElement);
      this.removeAttribute('open');
      this.releaseLock();
      this.listenForKeys(false);
      this.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
      });
      this.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      this.querySelectorAll('video').forEach((video) => video.pause());
      if (this.querySelector('#Product-Modal-Content')) {
        this.querySelector('#Product-Modal-Content').innerHTML = '';
      }
      if (this.querySelector('.compare-lightbox--data')) {
        this.querySelector('.compare-lightbox--data').innerHTML = '';
      }
      // Hand focus back to whatever had it; an overlay that opened on its own just lets go.
      if (this.overlay && hadFocus) {
        if (this.openedBy && this.openedBy !== document.body && this.openedBy.isConnected) {
          this.openedBy.focus({ preventScroll: true });
        } else {
          document.activeElement.blur();
        }
      }
    }

    // Keep the lock while another modal is still open.
    releaseLock() {
      if (!document.querySelector('modal-dialog[open]')) {
        document.body.classList.remove('overflow-hidden');
      }
    }

    listenForKeys(listen) {
      if (!this.overlay || this.designMode) return;
      const method = listen ? 'addEventListener' : 'removeEventListener';
      document[method]('keydown', this._onKeydown);
      document[method]('keyup', this._onKeyup);
    }

    trapFocus(event) {
      if (event.key !== 'Tab' || !this.hasAttribute('open')) return;
      const active = document.activeElement || document.body,
        outside = !this.contains(active);

      // Focus inside another open modal is that modal's to keep.
      if (outside && active.closest('modal-dialog[open]')) return;

      const dialog = this.querySelector('[role="dialog"]'),
        focusable = Array.from(dialog.querySelectorAll(overlayFocusable)).filter((element) => element.getClientRects().length);

      if (!focusable.length) {
        event.preventDefault();
        if (outside) dialog.focus();
        return;
      }
      const first = focusable[0],
        last = focusable[focusable.length - 1];

      if (event.shiftKey && (outside || active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    }

    // Overlays are remembered per section in localStorage. data-frequency is
    // the number of days before a pop-up returns; without it, once is enough.
    // Blocked storage reads as "not seen", so the age gate fails closed.
    suppressed() {
      let value = null;
      try {
        value = window.localStorage.getItem(this.id);
      } catch (error) {
        return false;
      }
      if (value === null) return false;

      const days = parseInt(this.dataset.frequency, 10) || 0;
      if (!days) return true;

      const time = parseInt(value, 10);
      return !isNaN(time) && Date.now() - time < days * 86400000;
    }

    store() {
      if (this.designMode) return;
      try {
        window.localStorage.setItem(this.id, String(Date.now()));
      } catch (error) {
        // Storage blocked: the overlay shows again on the next page view.
      }
    }
  }
  customElements.define('modal-dialog', ModalDialog);
}
if (!customElements.get('modal-opener')) {
  class ModalOpener extends HTMLElement {
    constructor() {
      super();

      const button = this.querySelector('button');

      if (!button) return;
      button.addEventListener('click', () => {
        const modal = document.querySelector(this.getAttribute('data-modal'));
        if (modal) modal.show(button);
      });
    }
  }
  customElements.define('modal-opener', ModalOpener);
}
